import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// We use JWT via Authorization header (localStorage 'pp-token').
// Cookies are only needed for the Emergent Google OAuth callback (handled explicitly with withCredentials).
axios.defaults.withCredentials = false;

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

export function AppProvider({ children }) {
  const [mode, setMode] = useState(() => localStorage.getItem('pp-mode') || 'cyber');
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    document.body.classList.remove('mode-cyber', 'mode-storybook');
    document.body.classList.add(`mode-${mode}`);
    localStorage.setItem('pp-mode', mode);
    if (mode === 'storybook') {
      document.body.style.backgroundImage =
        "linear-gradient(rgba(255,251,242,0.72), rgba(255,251,242,0.55)), url('/assets/castle-bg.png')";
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.backgroundPosition = 'right top';
    } else {
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundAttachment = '';
      document.body.style.backgroundPosition = '';
    }
  }, [mode]);

  const checkAuth = useCallback(async () => {
    // Skip if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) { setAuthChecked(true); return; }
    try {
      const token = localStorage.getItem('pp-token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await axios.get(`${API}/auth/me`, { headers });
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const toggleMode = () => setMode(m => (m === 'cyber' ? 'storybook' : 'cyber'));

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.removeItem('pp-token');
    setUser(null);
  };

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    if (r.data.token) localStorage.setItem('pp-token', r.data.token);
    setUser(r.data);
    return r.data;
  };

  const signup = async (email, password, name) => {
    const r = await axios.post(`${API}/auth/signup`, { email, password, name });
    if (r.data.token) localStorage.setItem('pp-token', r.data.token);
    setUser(r.data);
    return r.data;
  };

  return (
    <AppCtx.Provider value={{ mode, toggleMode, user, setUser, authChecked, checkAuth, login, signup, logout, API }}>
      {children}
    </AppCtx.Provider>
  );
}
